import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Facebook, Instagram, Linkedin } from "lucide-react";
import { SITE } from "@/config/site";
import useSectionNav from "@/hooks/useSectionNav";
import { MapleMark } from "@/components/MapleMark";
import PrivacyDialog from "@/components/PrivacyDialog";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", id: "services" },
  { label: "Why Postings", id: "why" },
  { label: "About", id: "about" },
  { label: "Contact", to: "/contact" },
];

const TOOLS = [
  { label: "Client Manager", url: SITE.apps.clientManager.url, testid: "footer-app-client-manager" },
  { label: "PostWage Payroll", url: SITE.apps.postWage.url, testid: "footer-app-postwage" },
  { label: "Book a Consultation", url: SITE.bookingUrl, testid: "footer-booking" },
  { label: "Pay an Invoice", to: "/contact", testid: "footer-pay-invoice" },
];

const SOCIALS = [
  { key: "facebook", icon: Facebook, label: "Facebook" },
  { key: "instagram", icon: Instagram, label: "Instagram" },
  { key: "linkedin", icon: Linkedin, label: "LinkedIn" },
];

export default function Footer() {
  const go = useSectionNav();
  const [legal, setLegal] = useState(null);
  const year = new Date().getFullYear();

  return (
    <footer className="noise-dark border-t border-white/10 bg-brand-deep pb-10 pt-16 text-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <p className="font-cabinet text-2xl font-extrabold tracking-tight">Postings Inc.</p>
            <p className="mt-2 text-sm text-white/50">{SITE.tagline}</p>
            <div className="mt-6 flex gap-3" data-testid="footer-socials">
              {SOCIALS.map(({ key, icon: Icon, label }) =>
                SITE.socials[key] ? (
                  <a
                    key={key}
                    href={SITE.socials[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Postings Inc. on ${label}`}
                    data-testid={`footer-social-${key}`}
                    className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-accent hover:text-accent"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ) : (
                  <span
                    key={key}
                    title={`${label} — coming soon`}
                    aria-disabled="true"
                    data-testid={`footer-social-${key}`}
                    className="grid h-10 w-10 cursor-not-allowed place-items-center rounded-full border border-white/10 text-white/25"
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                )
              )}
            </div>
            <div className="mt-7">
              <a
                href={SITE.sisterFirm.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-sister-firm"
                className="inline-flex h-10 items-center gap-1.5 rounded-full border border-white/20 px-5 text-sm font-semibold text-white/80 transition-colors hover:border-accent hover:text-accent"
              >
                {SITE.sisterFirm.name}
                <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
              <p className="mt-2 text-xs text-white/40">Operating alongside the Ford Group organization.</p>
            </div>
          </div>

          <nav className="md:col-span-2" aria-label="Footer">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/40">Explore</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              {NAV.map((l) => (
                <li key={l.label}>
                  <button
                    onClick={() => go(l)}
                    data-testid={`footer-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-white/70 transition-colors hover:text-white"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div className="md:col-span-3">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/40">Client Tools</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              {TOOLS.map((t) =>
                t.url ? (
                  <li key={t.label}>
                    <a
                      href={t.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={t.testid}
                      className="text-white/70 transition-colors hover:text-white"
                    >
                      {t.label} <span aria-hidden="true">↗</span>
                    </a>
                  </li>
                ) : (
                  <li key={t.label}>
                    <Link
                      to={t.to}
                      data-testid={t.testid}
                      className="text-white/70 transition-colors hover:text-white"
                    >
                      {t.label}
                    </Link>
                  </li>
                )
              )}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/40">Contact</p>
            <ul className="mt-4 space-y-2.5 text-sm text-white/70">
              <li>{SITE.address.street}</li>
              <li>
                {SITE.address.city}, {SITE.address.province}
              </li>
              <li>
                <a href={SITE.phoneHref} data-testid="footer-phone" className="transition-colors hover:text-white">
                  {SITE.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={`mailto:${SITE.email}`} data-testid="footer-email" className="transition-colors hover:text-white">
                  {SITE.email}
                </a>
              </li>
              {SITE.hours.map((h) => (
                <li key={h.days} className="text-white/45">
                  {h.days}: {h.time}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-6 text-xs text-white/40 sm:flex-row sm:items-center">
          <p data-testid="footer-copyright">© {year} Postings Inc. All Rights Reserved.</p>
          <div className="flex items-center gap-5">
            <button
              onClick={() => setLegal("privacy")}
              data-testid="footer-privacy-link"
              className="transition-colors hover:text-white"
            >
              Privacy Policy
            </button>
            <button
              onClick={() => setLegal("terms")}
              data-testid="footer-terms-link"
              className="transition-colors hover:text-white"
            >
              Terms of Use
            </button>
            <MapleMark className="h-3.5 w-3.5 text-accent" />
          </div>
        </div>
      </div>

      <PrivacyDialog open={!!legal} doc={legal || "privacy"} onOpenChange={(o) => !o && setLegal(null)} />
    </footer>
  );
}
